import { useAppSelector } from '@/shared/hooks/useStore';
import { useDataConsole } from '@/viewmodel/features/dataConsole/hooks/useDataConsole';
import { consolePath } from '@/viewmodel/features/dataConsole/mappers/workspaceSourcesPath';
import { type SourceProject, useEtlLiveUpdatesFor } from '@/viewmodel/features/etlSource';
import { selectCurrentWorkspace } from '@/viewmodel/features/workspace';
import { useDatasetContentViewModel } from '@/viewmodel/shared/sidebar';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export interface WorkspaceSourceScopeViewModel {
  readonly scope: SourceProject | null;
  readonly projectName: string;
  readonly isStore: boolean;
  readonly isFullScreenStep: boolean;
  readonly isNotFound: boolean;
  readonly isError: boolean;
  readonly backToSources: () => void;
  readonly openAccess: () => void;
}

export function useWorkspaceSourceScopeViewModel(): WorkspaceSourceScopeViewModel {
  const navigate = useNavigate();
  const { projectId = '' } = useParams<{ projectId: string }>();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const isFullScreenStep = useDatasetContentViewModel() === 'flush';
  const { view, isLoading, isError } = useDataConsole(workspace?.id);
  const project = [view.store, ...view.projects].find((candidate) => candidate?.id === projectId);
  const requestId = project?.requestId ?? '';
  const scope = useMemo(
    () => (requestId ? { projectId, requestId } : null),
    [projectId, requestId],
  );
  useEtlLiveUpdatesFor(scope ? [projectId] : []);

  return {
    scope,
    projectName: project?.name ?? '',
    isFullScreenStep,
    isStore: !!project && project.id === view.store?.id,
    isError,
    isNotFound: !isLoading && !isError && !!workspace && !project,
    backToSources: () => navigate(consolePath('sources')),
    openAccess: () => navigate(consolePath('access')),
  };
}
